/**
 * @file user-types.ts
 * @description Types et interfaces pour le système de gestion des utilisateurs.
 * Ce fichier définit la structure de données utilisée pour représenter les profils utilisateurs
 * et leurs composants, avec support pour les préférences et la gestion des thèmes.
 */

import type { IDateValue } from "../../../utils";
import { ConsentIds } from "../../consentManager/types/const";
import { ActiveLicense, ExpiredLicense } from "../../license/types/license.api";
import type { Settings } from "./settings";

// #region TYPES DE BASE
/**
 * Types simples et unions utilisés pour les profils utilisateurs
 */

/**
 * Types d'utilisateur possibles
 * @example 'ADMIN', 'USER', 'ENTERPRISE_USER', 'ENTERPRISE_MANAGER'
 */
enum UserType {
    ADMIN = "ADMIN",
    USER = "USER",
    ENTERPRISE_USER = "ENTERPRISE_USER",
    ENTERPRISE_MANAGER = "ENTERPRISE_MANAGER",
}

enum UserStatus {
    ACTIVE = "Active",
    HOLD = "Hold",
    DEACTIVATED = "Deactivated",
    ARCHIVED = "Archived",
}

type StatusDetail = {
    status: UserStatus;
    holdDate: Date | null;
    deactivatedDate: Date | null;
    archivedDate: Date | null;
};

// #endregion

// #region INTERFACES DE PROFIL UTILISATEUR

/**
 * Informations de base du profil utilisateur
 */
type Profile = {
    /** Date d'inscription de l'utilisateur */
    registrationDate?: IDateValue;
    /** Date de désinscription (null si l'utilisateur est actif) */
    unregistrationDate: IDateValue;
    /** Date et heure de la dernière connexion */
    lastLoginDate?: IDateValue;
    /** Adresse email de l'utilisateur */
    email: string;
    /** Nom affiché de l'utilisateur */
    displayName: string;
    /**
     * AB#1621 — Plancher figé de la fenêtre rétrospective d'analyse (borne basse, « 1ʳᵉ connexion »).
     * Posé set-once par le SDK en fin de 1ʳᵉ analyse. Lu par le pré-filtre `_processEmailsWithAI`.
     * Absent (null/undefined) tant que non posé → repli `registrationDate − byDay`.
     */
    analysisFloorDate?: IDateValue;
};

// #endregion

// #region INTERFACES DE LICENCES

/**
 * Élément de licence attribué à un utilisateur
 */
type UserLicenseItem = {
    /** Identifiant unique de l'attribution de licence */
    id: string;
    /** Identifiant de la licence attribuée */
    licenseId: string;
    /** Date de début de validité */
    startDate: string;
    /** Date de dernière mise à jour des tokens */
    lastTokenUpdate: string;
    /** Date d'expiration de la licence */
    expirationDate: string;
    /** Indique si la licence a expiré */
    expired: boolean;
};

/**
 * Licence utilisateur avec l'ensemble des licences attribuées
 */
type UserLicense = {
    /** Identifiant unique de la licence utilisateur */
    id: string;
    /** Identifiant de l'utilisateur */
    userId: string;
    /** Email de l'utilisateur */
    userMail: string;
    /** Liste des licences attribuées à l'utilisateur */
    licenses: UserLicenseItem[];
};

enum FeatureFlag {
    SETTINGS_DISPLAY_LICENSEMANAGEMENT = "SETTINGS_DISPLAY_LICENSEMANAGEMENT",
    SETTINGS_DISPLAY_SIGNATURE = "SETTINGS_DISPLAY_SIGNATURE",
    SETTINGS_DISPLAY_ADMINDASHBOARD = "SETTINGS_DISPLAY_ADMINDASHBOARD",
    SETTINGS_DISPLAY_MODELMANAGEMENT = "SETTINGS_DISPLAY_MODELMANAGEMENT",
    ONBOARDING_DISPLAY_WELCOMEVIDEO = "ONBOARDING_DISPLAY_WELCOMEVIDEO",
    ONBOARDING_DISPLAY_NEWFEATURE = "ONBOARDING_DISPLAY_NEWFEATURE",
    MAIL_ENABLE_DEEPFOCUS = "MAIL_ENABLE_DEEPFOCUS",
    MAIL_ENABLE_ANALYSIS = "MAIL_ENABLE_ANALYSIS",
    ASSISTANT_ENABLE_VOCAL = "ASSISTANT_ENABLE_VOCAL",
    ASSISTANT_ENABLE_PROMPT = "ASSISTANT_ENABLE_PROMPT",
    AGENDA_ENABLE_ACCESS = "AGENDA_ENABLE_ACCESS",
    DEVICESTORAGE_ENABLE_ACCESS = "DEVICESTORAGE_ENABLE_ACCESS",
    DASHBOARD_DISPLAY_CO2 = "DASHBOARD_DISPLAY_CO2",
    SYSTEM_ENABLE_LOGS = "SYSTEM_ENABLE_LOGS",
    SUPPORT_ENABLE_TICKETCREATION = "SUPPORT_ENABLE_TICKETCREATION",
    CUSTOMVIEW_ENABLE_CREATION = "CUSTOMVIEW_ENABLE_CREATION",
}

/**
 * Détails d'un type de licence disponible
 */
type LicenseDetails = {
    /** Identifiant unique du type de licence */
    id: string;
    /** Nom de la licence */
    name: string;
    /** DEPRECATED - Replaced by monthlyLimit.byToken
     * Nombre de tokens mensuels alloués */
    monthlyTokenCount: number;
    /** DEPRECATED - Replaced by monthlyLimit.byAnalysedMails
     * Nombre d'emails traités autorisés par mois */
    authorizedProcessedMailPerMonth: number;
    /** Durée de validité ("MONTHLY", "YEAR", "FOREVER") */
    validity: "MONTHLY" | "YEAR" | "FOREVER";
    /** Analyse initiale autorisée */
    initialAnalysis: {
        /** Nombre d'analyses par email */
        byMail: number;
        /** Nombre d'analyses par jour */
        byDay: number;
    };
    /** Limites mensuelles */
    monthlyLimit: {
        /** Limite par token */
        byToken: number;
        /** Limite par mails */
        byAnalysedMails: number;
    };
    /** Limites de l'historique */
    historyLimit: {
        /** Limite par email */
        byMail: number;
        /** Limite par mois */
        byMonth: number;
    };
    /** Flags de fonctionnalités activées */
    featureFlags: FeatureFlag[];
    /** Indique si c'est une licence entreprise */
    isEnterpriseLicenceScope: boolean;
    /** Limite de création de CustomView */
    customViewLimit: number;
};

/**
 * Détails complets des licences de l'utilisateur, construits à partir de la réponse bootstrap.
 * Remplace l'ancien modèle (userLicense / teamLicense / allLicensesDetails).
 */
type UserLicensesDetails = {
    /** Tokens dépensés ce mois — issu de userProfile.monthlySpentTokenCount */
    monthlySpentTokenCount: number;
    /** Tokens mensuels alloués — issu de active[0]?.detail.monthlyLimit.byToken */
    monthlyTokenCount: number;
    /** Licences actives avec leur détail complet embarqué */
    active: ActiveLicense[];
    /** Licences expirées */
    expired: ExpiredLicense[];
    /** Licences à venir */
    upcoming: ExpiredLicense[];
};

// #endregion

// #region INTERFACE PRINCIPALE DU PROFIL UTILISATEUR

/**
 * Profil utilisateur complet
 * Représentation complète d'un utilisateur dans le système
 */
type UserProfile = {
    /** Identifiant unique du profil */
    id: string;
    /** Identifiant de l'utilisateur (généralement associé au système d'authentification) */
    userId: string;
    /** Identifiant du tenant (pour les environnements multi-tenant) */
    tenantId: string | null;
    /** Identifiant de l'équipe pour la gestion des licences entreprise */
    teamId: string | null;
    /** Type d'utilisateur */
    userType: UserType;
    /** Prénom */
    firstName: string;
    /** Nom de famille */
    lastName: string;
    /** Profile OAuth */
    profile: Profile;
    /** Paramètres et préférences de l'utilisateur */
    settings: Settings;
    /** Indique si l'utilisateur est désactivé */
    isUserDisabled: boolean;
    /** DEPRECATED - Indique si l'onboarding est terminé */
    isOnboardingDone: boolean;
    /** Nombre de mails analysés ce mois */
    monthlyAnalyzedMailCount: number;
    /** Nombre de tokens dépensés ce mois */
    monthlySpentTokenCount: number;
    /** Prochaine date de renouvellement mensuel des quotas (ISO 8601, null si registrationDate absente) */
    nextQuotaRenewalDate: string | null;
    /** Détail du statut du compte utilisateur (Active, Hold, Deactivated, Archived) */
    statusDetail: StatusDetail;
    /** URL de la photo de profil */
    photoURL?: string;
    /** Numéro de téléphone */
    phoneNumber?: string;
    /** Liste des consentements */
    userConsents?: UserConsents[];
    /** Stock le contexte utilisateur */
    lastContext?: EnrichUserContext;
};

type UserConsents = {
    consentId: ConsentIds;
    enabled: boolean;
};

type EnrichUserContext = {
    updatedAt: string;
    context: string;
};

type UserSignature = {
    id: string;
    userId: string;
    signatureTitle?: string;
    name?: string;
    jobTitle?: string;
    email?: string;
    companyName?: string;
    phone?: string;
    website?: string;
    other?: string;
    avatar?: string;
    isFavorite: boolean;
};

// #endregion

// Exports
export type {
    Profile,
    UserLicenseItem,
    UserLicense,
    LicenseDetails,
    UserLicensesDetails,
    UserProfile,
    UserSignature,
    UserConsents,
    StatusDetail,
};

export { FeatureFlag, UserType, UserStatus };
